import { useFormContext } from 'react-hook-form'
import { IntegrationSettings } from '../../../schema'
import { Col, Row } from 'react-bootstrap'
import { SelectDropdown, SelectOption } from '../../../../../../../components/SelectDropdown/SelectDropdown'
import { getIntegrationDisplayName, IntegrationServiceId } from '../../../../../../../common/integrations'
import { DetailedCRMAccountIntegrationMapping, MappingType } from '../../../../../../../models/Account'
import { useFilterIntegrationMappingTemplatesQuery, useGetSupportedObjectsQuery } from '../../../../../../../store/integrations/integrations'
import { useCallback, useMemo } from 'react'
import { selectAccountId } from '../../../../../../../store/auth/authSlice'
import { useAppSelector } from '../../../../../../../store/hooks'
import { useGetIntegrationAdminSettings } from '../../../hooks'
import { getContactObjectLockedBy, isSameContactObject } from '../../../utils'
import { ContactObjectLockNotice } from '../../../ContactObjectLockNotice'

const CUSTOM_CONTACT_OBJECT_OPTION_ID = 'customContactObject'

interface SelectContactTypeSectionProps {
  onChange: () => void
  serviceId: IntegrationServiceId
  disabled?: boolean
}

export const SelectContactTypeSection: React.FC<SelectContactTypeSectionProps> = ({ onChange, serviceId, disabled }) => {
  const {
    watch,
    setValue,
    formState: { errors },
  } = useFormContext<IntegrationSettings>()
  const accountId = useAppSelector(selectAccountId)
  const currentObjectName = watch('mappings.contact.objectName')
  const currentMappingType = watch('mappings.contact.mappingType')

  const isCustom = useMemo(() => currentMappingType === MappingType.CUSTOM, [currentMappingType])

  const { data: inboundMappingTemplatePaths, isFetching: isInboundMappingTemplatePathsFetching } = useFilterIntegrationMappingTemplatesQuery({
    active: true,
    sourcePlatform: serviceId,
    destinationObject: 'contact',
  })

  const sessionLinkings = watch('mappings.session.contactLinks')
  const sessionFilterGroup = watch('mappings.session.filterGroup')

  const taskLinkings = watch('mappings.task.contactLinks')
  const taskFilterGroup = watch('mappings.task.filterGroup')

  const additionalObjectsMappings = watch('mappings.additionalObjects') as DetailedCRMAccountIntegrationMapping[]

  const clearContactLinksIfNeeded = useCallback(() => {
    if ((sessionLinkings && sessionLinkings.length > 0) || (sessionFilterGroup?.filters?.length ?? 0) > 0) {
      setValue('mappings.session.contactLinks', [], { shouldDirty: true })
      setValue('mappings.session.filterGroup', null, { shouldDirty: true })
    }
    if ((taskLinkings && taskLinkings.length > 0) || (taskFilterGroup?.filters?.length ?? 0) > 0) {
      setValue('mappings.task.contactLinks', [], { shouldDirty: true })
      setValue('mappings.task.filterGroup', null, { shouldDirty: true })
    }
    if (additionalObjectsMappings && additionalObjectsMappings.length > 0) {
      const newAdditionalObjects = additionalObjectsMappings.map((obj: DetailedCRMAccountIntegrationMapping) => ({
        ...obj,
        contactLinks: [],
        filterGroup: obj.filterGroup
          ? {
              ...obj.filterGroup,
              filters: obj.filterGroup.filters.map((f) => ({
                ...f,
                expressions: f.expressions.filter((e) => e.field !== 'ContactId' && e.source.field !== 'contactId'),
              })),
            }
          : obj.filterGroup,
      }))
      setValue('mappings.additionalObjects', newAdditionalObjects, { shouldDirty: true })
    }
  }, [setValue, sessionLinkings, sessionFilterGroup, taskLinkings, taskFilterGroup, additionalObjectsMappings])

  const { hasIntegration, currentIntegrationAccountConfig } = useGetIntegrationAdminSettings(serviceId)
  const contactObjectLockedBy = getContactObjectLockedBy(currentIntegrationAccountConfig)
  const isContactObjectLocked = contactObjectLockedBy.length > 0
  const savedContactObjectName = currentIntegrationAccountConfig?.mappings?.contact?.objectName
  const isAllowedContactObject = useCallback(
    (objectName: string) => !isContactObjectLocked || isSameContactObject(serviceId, savedContactObjectName, objectName),
    [isContactObjectLocked, savedContactObjectName, serviceId],
  )

  const contactCRMTypeOptions: SelectOption[] = useMemo(() => {
    if (!inboundMappingTemplatePaths) {
      return []
    }
    return inboundMappingTemplatePaths
      .filter((i) => i.sourceObject === CUSTOM_CONTACT_OBJECT_OPTION_ID || isAllowedContactObject(i.sourceObject))
      .map((i) => ({ id: i.sourceObject, label: i.name }))
  }, [inboundMappingTemplatePaths, isAllowedContactObject])

  const { data: supportedObjects, isLoading: isLoadingSupportedObjects } = useGetSupportedObjectsQuery(
    { accountId, connector: serviceId },
    { skip: !accountId || !serviceId || !hasIntegration },
  )

  const customContactOption = useMemo(() => supportedObjects?.find((opt) => opt.name === currentObjectName) || null, [supportedObjects, currentObjectName])
  const contactTypeSelectOptions: SelectOption[] = useMemo(
    () => supportedObjects?.filter((obj) => isAllowedContactObject(obj.name)).map((obj) => ({ id: obj.name, label: `${obj.label} (${obj.name})` })) || [],
    [supportedObjects, isAllowedContactObject],
  )

  const savedContactObjectLabel = useMemo(() => {
    if (!savedContactObjectName) {
      return undefined
    }
    const label =
      currentIntegrationAccountConfig?.mappings?.contact?.mappingType === MappingType.CUSTOM
        ? supportedObjects?.find((obj) => obj.name === savedContactObjectName)?.label
        : inboundMappingTemplatePaths?.find((i) => i.sourceObject === savedContactObjectName)?.name
    return label ?? savedContactObjectName
  }, [savedContactObjectName, currentIntegrationAccountConfig, supportedObjects, inboundMappingTemplatePaths])

  const clearContactLinksIfObjectChanges = useCallback(
    (nextObjectName: string) => {
      if (!isSameContactObject(serviceId, currentObjectName || savedContactObjectName, nextObjectName)) {
        clearContactLinksIfNeeded()
      }
    },
    [serviceId, currentObjectName, savedContactObjectName, clearContactLinksIfNeeded],
  )

  const predefinedContactOption: SelectOption | null = useMemo(() => {
    if (isCustom) {
      return { id: CUSTOM_CONTACT_OBJECT_OPTION_ID, label: 'Custom Contact Object' }
    }
    return contactCRMTypeOptions.find((opt) => opt.id === currentObjectName) || null
  }, [contactCRMTypeOptions, currentObjectName, isCustom])
  const handleCustomOptionSelect = (option: { id: string; label: string }) => {
    if (option.id === currentObjectName && isCustom) {
      return
    }
    clearContactLinksIfObjectChanges(option.id)
    setValue('mappings.contactFields', null, { shouldDirty: true })
    setValue('mappings.contact', { objectName: option.id }, { shouldDirty: true, shouldValidate: true })
    setValue('mappings.contact.mappingType', MappingType.CUSTOM, {
      shouldDirty: true,
    })
    onChange()
  }

  const handlePredefinedOptionSelect = (option: { id: string; label: string }) => {
    if (option.id === currentObjectName && !isCustom) {
      return
    }
    setValue('mappings.contactFields', null, { shouldDirty: true })
    if (option.id === CUSTOM_CONTACT_OBJECT_OPTION_ID) {
      setValue('mappings.contact', { objectName: '' }, { shouldDirty: true, shouldValidate: true })
      setValue('mappings.contact.mappingType', MappingType.CUSTOM, {
        shouldDirty: true,
        shouldValidate: true,
      })
      onChange()
      return
    }
    clearContactLinksIfObjectChanges(option.id)
    setValue('mappings.contact', { objectName: option.id }, { shouldDirty: true, shouldValidate: true })
    setValue('mappings.contact.mappingType', MappingType.PREDEFINED, {
      shouldDirty: true,
      shouldValidate: true,
    })
    onChange()
  }

  const fieldError = errors.mappings?.contact?.objectName
  const objectError = errors.mappings?.contact

  return (
    <>
      <Row className='mb-2'>
        <Col>
          <h4>{getIntegrationDisplayName(serviceId)} contact object*</h4>
          <p className='zocks-text-gray-600'>Select your {getIntegrationDisplayName(serviceId)} contact object type.</p>
        </Col>
      </Row>
      <Row>
        <Col md={6}>
          <SelectDropdown
            initialSelectedOption={predefinedContactOption}
            options={contactCRMTypeOptions}
            onOptionSelected={handlePredefinedOptionSelect}
            loading={isInboundMappingTemplatePathsFetching}
            disabled={disabled}
          />
          {fieldError && <div className='text-danger mt-1 small'>{fieldError.message}</div>}
          {objectError && <div className='text-danger mt-1 small'>{objectError.message}</div>}
          <ContactObjectLockNotice serviceId={serviceId} contactObjectLabel={savedContactObjectLabel} contactObjectLockedBy={contactObjectLockedBy} />
        </Col>
        {isCustom && (
          <Col md={6}>
            <SelectDropdown
              options={contactTypeSelectOptions}
              initialSelectedOption={
                customContactOption ? { id: customContactOption.name, label: `${customContactOption.label} (${customContactOption.name})` } : null
              }
              placeholder='Select Object Type'
              onOptionSelected={handleCustomOptionSelect}
              searchable
              loading={isLoadingSupportedObjects}
              disabled={disabled}
            />
          </Col>
        )}
      </Row>
    </>
  )
}
